import { useTranslation } from 'react-i18next';

import { IconComponent } from 'src/components/Icons/TextIcon';
import {
    OrganizationModuleType,
    isOrganizationModuleEmployeeReadAccessEnabled,
    isOrganizationModuleEnabled,
} from 'src/domain/organization';
import { isOrganizationAdministrator } from 'src/domain/user';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { ModuleEmployeeReadAccessSettings } from './ModuleEmployeeReadAccessSettings';
import { ModuleMembersSettings } from './ModuleMembersSettings';
import { ModulePageLayout } from './ModulePageLayout';
import { ModuleUnavailableState } from './ModuleUnavailableState';

type ModuleSettingsScreenProps = {
    basePath: string;
    icon: IconComponent;
    moduleType: OrganizationModuleType;
    moduleName: string;
    memberDescription: string;
    /** Analytics is shared only with members, so it has no "all employees" switch. */
    allowEmployeeReadAccess?: boolean;
};

export const ModuleSettingsScreen = ({
    basePath,
    icon,
    moduleType,
    moduleName,
    memberDescription,
    allowEmployeeReadAccess = true,
}: ModuleSettingsScreenProps) => {
    const { t } = useTranslation();
    const user = useSignedInUser();
    const { organizationId, organization, isLoading } =
        useCurrentOrganization();
    const enabled = isOrganizationModuleEnabled(organization, moduleType);
    const canManageSettings =
        !user?.supportMode && isOrganizationAdministrator(user);

    if (isLoading) {
        return (
            <div className="grid justify-center py-10 text-woodyBrown">
                {t('common.loading')}
            </div>
        );
    }

    if (!enabled) {
        return (
            <ModuleUnavailableState
                icon={icon}
                moduleName={moduleName}
                reason="notEnabled"
            />
        );
    }

    const module = organization?.modules?.find(
        entry => entry.type === moduleType,
    );

    return (
        <ModulePageLayout
            basePath={basePath}
            canManageSettings={canManageSettings}
        >
            <div className="h-full overflow-y-auto pb-8">
                <div className="mb-5">
                    <h2 className="font-logo text-2xl">
                        {t('modules.modulePages.settings')}
                    </h2>
                    <p className="mt-1 text-sm text-woodyBrown text-opacity-60">
                        {t('modules.modulePages.settingsDescription', {
                            module: moduleName,
                        })}
                    </p>
                </div>
                {organizationId && (
                    <div className="space-y-5">
                        {allowEmployeeReadAccess && (
                            <ModuleEmployeeReadAccessSettings
                                organizationId={organizationId}
                                moduleType={moduleType}
                                moduleName={moduleName}
                                enabled={isOrganizationModuleEmployeeReadAccessEnabled(
                                    organization,
                                    moduleType,
                                )}
                            />
                        )}
                        <ModuleMembersSettings
                            organizationId={organizationId}
                            currentUserId={user?.id}
                            organization={organization}
                            moduleType={moduleType}
                            module={module}
                            canManage={canManageSettings}
                            description={memberDescription}
                        />
                    </div>
                )}
            </div>
        </ModulePageLayout>
    );
};
